import { MarketingKitGridItem } from "@/components/MarketingKit/MarketingKitGridItem";
import { useGenerateMarketingKit } from "@/hooks/api/marketing-kit/useGenerateMarketingKit";
import { NoCourses } from "@/partials/course/NoCourses";
import { hasCoursesQueryOptions } from "@/queries/courses/hasCoursesQuery";
import {
  courseMarketingKitQueryOptions,
  useCourseMarketingKit,
} from "@/queries/marketing-kit/courseMarketingKitQuery";
import { keepPreviousData, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@vixi/router";
import { courseBaseByIdQueryOptions, allCoursesQueryOptions } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import { ShallowCourse, Paginated } from "@vixi/types";
import {
  Autocomplete,
  Button,
  FieldLabel,
  FormDescription,
  FormHeading,
  LoadingScreen,
  LoadingSpinner,
  PageMainContent,
  useGoBack,
} from "@vixi/ui";
import { useDebounce } from "@vixi/utils";
import { useEffect, useMemo } from "react";
import { useController, useForm } from "react-hook-form";

// Constants
const routeId = "/_app/marketing-kit/" satisfies RouterIds;
const Route = router.routesById[routeId];

export const MarketingKitIndexPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loaderDeps: ({ search }) => search,
  loader: async ({ context: { queryClient }, deps: { courseId } }) => {
    if (courseId) {
      queryClient.ensureQueryData(courseMarketingKitQueryOptions(queryClient, courseId));
    }
  },
};

interface MarketingKitFormValues {
  searchText?: string;
}

function RouteComponent() {
  useGoBack(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { courseId } = Route.useSearch();
  const queryClient = useQueryClient();

  const { watch, control, setValue } = useForm<MarketingKitFormValues>({
    defaultValues: {
      searchText: "",
    },
  });
  const debouncedSearchTerm = useDebounce(watch("searchText"), 300);

  const {
    mutateAsync: generateMarketingKit,
    isPending: isGeneratingMarketingKit,
    isSuccess: isMarketingKitGenerated,
  } = useGenerateMarketingKit();

  const handleGenerateMarketingKit = async () => {
    if (courseId) {
      await generateMarketingKit(courseId);
    }
  };

  const {
    data: { selectedCourse, courses },
    isPending,
    allCoursesQuery: { isPending: isAllCoursesPending },
  } = useQueries({
    queries: [
      courseBaseByIdQueryOptions(queryClient, courseId),
      {
        ...allCoursesQueryOptions({ pageNumber: 1, pageSize: 20, searchTerm: debouncedSearchTerm }),
        placeholderData: keepPreviousData,
      },
    ],
    combine: (results) => {
      const [courseBaseQuery, allCoursesQuery] = results;

      const isPending = results.some((result) => result.isEnabled && result.isPending);

      const data = ((): {
        selectedCourse?: ShallowCourse;
        courses?: Paginated<ShallowCourse>;
      } => {
        if (allCoursesQuery.isSuccess && courseBaseQuery.isSuccess) {
          const foundCourse = allCoursesQuery.data.items?.find((course) => course.id === courseId);
          return {
            selectedCourse: courseBaseQuery.data,
            courses: {
              ...allCoursesQuery.data,
              items: foundCourse
                ? allCoursesQuery.data.items
                : [courseBaseQuery.data, ...(allCoursesQuery.data.items ?? [])],
            },
          };
        } else if (allCoursesQuery.isSuccess) {
          const foundCourse = allCoursesQuery.data.items?.find((course) => course.id === courseId);
          return {
            selectedCourse: foundCourse,
            courses: allCoursesQuery.data,
          };
        } else if (courseBaseQuery.isSuccess) {
          return {
            selectedCourse: courseBaseQuery.data,
            courses: {
              items: [courseBaseQuery.data],
              totalCount: 1,
              totalPages: 1,
              pageNumber: 1,
              hasNextPage: false,
              hasPreviousPage: false,
            },
          };
        }

        return {};
      })();

      return {
        data,
        isPending,
        courseBaseQuery,
        allCoursesQuery,
      };
    },
  });

  const { data: hasCourses, isSuccess: isHasCoursesSuccess } = useQuery(hasCoursesQueryOptions());

  // Auto-select first course if no courseId is selected but courses are available
  useEffect(() => {
    // Only auto-select when there's no search term (initial load)
    if (
      !courseId &&
      !debouncedSearchTerm &&
      courses?.items &&
      courses.items.length > 0 &&
      !isPending
    ) {
      const firstCourse = courses.items[0];
      if (firstCourse?.id) {
        navigate({
          from: Route.fullPath,
          replace: true,
          search: {
            courseId: firstCourse.id,
          },
        });
      }
    }
  }, [courseId, courses, isPending, navigate, debouncedSearchTerm]);

  const { data: courseMarketingKit, isFetching: isFetchingMarketingKit } =
    useCourseMarketingKit(courseId);

  const isMarketingKitAvailable = useMemo(() => {
    // Only show items if they have actual URLs (not just loading placeholders)
    return !!courseMarketingKit && courseMarketingKit.some((item) => item && item.url);
  }, [courseMarketingKit]);

  const {
    field: { onChange: onSearch, ...searchTextController },
  } = useController({
    control,
    name: "searchText",
  });

  return (
    <PageMainContent ps="medium">
      <div className="p-0 bg-white rounded flex items-center justify-between">
      <FormHeading heading="MarketingKit.Title">
        <FormDescription className="">{t("MarketingKit.Subtitle")}</FormDescription>
      </FormHeading>
      <img className="w-[95px] h-[84px] me-7.5" src="/images/marketing-kit/marketkit.png" alt="Marketing Kit" />
      </div>
      {hasCourses ? (
        <div className="mt-7.5">
          <FieldLabel>{t("MarketingKit.SelectCourse")}</FieldLabel>

          <Autocomplete
            value={selectedCourse ?? null}
            getOptionLabel={(option) => option.title ?? ""}
            getOptionKey={(option) => `${option.id}`}
            options={courses?.items ?? []}
            onChange={(course) => {
              navigate({
                from: Route.fullPath,
                replace: true,
                search: {
                  courseId: course?.id ?? undefined,
                },
              });
            }}
            afterOpenChange={(value) => {
              if (!value) {
                setValue("searchText", "");
              }
            }}
            onSearch={onSearch}
            loading={isPending}
          />
        </div>
      ) : (
        isHasCoursesSuccess && <NoCourses />
      )}

      {!courseId && isAllCoursesPending && !courses && <LoadingScreen />}

      {courseMarketingKit ? (
        <div className="mt-10 flex gap-22.5">
          {isGeneratingMarketingKit || isMarketingKitAvailable ? (
            // Display marketing kit items if generating or available
            courseMarketingKit.map((item, i) => (
              <MarketingKitGridItem key={i} item={item} course={selectedCourse} />
            ))
          ) : (
            // No items available
            <div className="mx-auto mt-17.5 flex flex-col items-center gap-5">
              <h4>{t("MarketingKit.NoItems")}</h4>
              <Button
                variant="contained"
                size="large"
                onClick={handleGenerateMarketingKit}
                disabled={isGeneratingMarketingKit}
              >
                {t("Action.GenerateMarketingKit")}
              </Button>
            </div>
          )}
        </div>
      ) : (
        (isFetchingMarketingKit || isGeneratingMarketingKit) && (
          <div className="m-auto">
            <LoadingSpinner />
          </div>
        )
      )}
    </PageMainContent>
  );
}
